import React, { FC, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '../../../Form/Button/button.scomponent';
import { Input } from '../../../Form/Input/input.component';

interface IPasswordData {
  password: string;
  password_confirmation: string;
}

interface IDialogChangePasswordProps {
  open: boolean;
  title?: string;
  description?: string;
  onConfirm: (data: IPasswordData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  forceReset?: boolean;
}

const DialogChangePassword: FC<IDialogChangePasswordProps> = ({
  open,
  title = 'Alterar Senha',
  description = 'Digite sua nova senha. Ela deve conter no mínimo 6 caracteres, incluindo números, pelo menos uma letra maiúscula e um caractere especial.',
  onConfirm,
  onCancel,
  isLoading = false,
  forceReset = false,
}) => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<IPasswordData>({
    mode: 'onChange',
    defaultValues: {
      password: '',
      password_confirmation: '',
    },
  });

  const password = watch('password');

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = (data: IPasswordData) => {
    onConfirm(data);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  // Validation rules
  const passwordValidation = {
    required: 'A senha é obrigatória',
    minLength: {
      value: 6,
      message: 'A senha deve ter no mínimo 6 caracteres',
    },
    validate: {
      hasNumber: (value: string) =>
        /\d/.test(value) || 'A senha deve conter pelo menos um número',
      hasUpperCase: (value: string) =>
        /[A-Z]/.test(value) || 'A senha deve conter pelo menos uma letra maiúscula',
      hasSpecialChar: (value: string) =>
        /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value) || 'A senha deve conter pelo menos um caractere especial',
    },
  };

  const confirmPasswordValidation = {
    required: 'A confirmação de senha é obrigatória',
    validate: (value: string) =>
      value === password || 'As senhas não coincidem',
  };

  return (
    <Dialog
      open={open}
      onClose={forceReset ? undefined : handleCancel}
      aria-labelledby="change-password-dialog-title"
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-paper': { padding: '24px', zIndex: 999999999999 } }}
    >
      <DialogTitle>
        <Typography fontSize="24px" fontWeight="600">
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: '16px', marginBottom: '24px' }}>
          {description}
        </DialogContentText>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Controller
            name="password"
            control={control}
            rules={passwordValidation}
            render={({ field }) => (
              <Input
                {...field}
                type="password"
                label="Nova Senha"
                placeholder="Digite sua nova senha"
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={isLoading}
                autoFocus
              />
            )}
          />

          <Controller
            name="password_confirmation"
            control={control}
            rules={confirmPasswordValidation}
            render={({ field }) => (
              <Input
                {...field}
                type="password"
                label="Confirmar Nova Senha"
                placeholder="Confirme sua nova senha"
                error={!!errors.password_confirmation}
                helperText={errors.password_confirmation?.message}
                disabled={isLoading}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 0 0 0' }}>
        {!forceReset && (
          <Button onClick={handleCancel} variant="text" disabled={isLoading}>
            Cancelar
          </Button>
        )}
        <Button 
          onClick={handleSubmit(onSubmit)} 
          variant="contained"
          disabled={!isValid || isLoading}
          isLoading={isLoading}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { DialogChangePassword };
